import {
  addOrReplaceAndRunAnalysisStep,
  deleteAnalysisSteps,
  updateAndRunAnalysisStep,
} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {DataReference} from 'analysis-tool/ui/main-view/MainViewTypes';
import {
  DataFrameRetypeColumnOptions,
  RetypeColumnType,
} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DeletePipelineStepsBehavior} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {withLoadingScreen} from 'util/Promises';

import {
  RightSidebarItemState,
  setRightSidebarItemIsUpdating,
  updateRightSidebarItemOnAddingStep,
} from './DataFrameHeaderMenuUtil';

const LOADING_MESSAGE = 'Converting column formats';

async function updateRetypeColumnStep(
  analysis: PipelineGraphQL,
  analysisClientStateId: string,
  retypeColumnStep: GetAnalysisPipelineStep,
  systemName: string,
  newColumnType?: RetypeColumnType
): Promise<void> {
  const previousRetypeColumns = retypeColumnStep.config?.variables.find(
    (variable) => variable.name === 'retypeColumns'
  )?.value as Array<{column: string; type: RetypeColumnType}> | undefined;
  const columnsConfigToKeep = previousRetypeColumns
    ? previousRetypeColumns.flat().filter(({column}) => column !== systemName)
    : [];
  const retypeColumns =
    newColumnType != null
      ? [...columnsConfigToKeep, {column: systemName, type: newColumnType}]
      : columnsConfigToKeep;
  if (retypeColumns.length === 0) {
    await deleteAnalysisSteps(
      analysis,
      {
        stepIds: [retypeColumnStep.id],
        behavior: DeletePipelineStepsBehavior.NO_INFERENCE,
      },
      analysisClientStateId
    );
  } else {
    await updateAndRunAnalysisStep(analysis.id, {
      stepId: retypeColumnStep.id,
      stepConfig: {retypeColumns},
    });
  }
}

async function addRetypeColumnStep(
  analysis: PipelineGraphQL,
  columnSystemName: string,
  newColumnType: RetypeColumnType,
  inputDataReference: DataReference,
  inputDataFrameId?: string,
  outputDataReferences?: Array<DataReference>,
  rightSidebarItemState?: RightSidebarItemState
): Promise<void> {
  if (rightSidebarItemState) {
    setRightSidebarItemIsUpdating(rightSidebarItemState);
  }
  const newStep = await addOrReplaceAndRunAnalysisStep(analysis.id, {
    stepType: PipelineStepType.RETYPE_COLUMNS,
    stepVersion: 0,
    stepConfig: {retypeColumns: [{column: columnSystemName, type: newColumnType}]},
    stepInputParents: {[SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [inputDataReference]},
    ...(inputDataFrameId == null ? {} : {stepInput: {inputDataFrameId}}),
    ...(outputDataReferences == null
      ? {}
      : {stepOutputChildren: {[SINGLE_DATASET_OUTPUT_STEP_DATA_NAME]: outputDataReferences}}),
  });
  if (rightSidebarItemState) {
    updateRightSidebarItemOnAddingStep(newStep, rightSidebarItemState);
  }
}

export function getRetypeColumnOptions(
  analysis: PipelineGraphQL,
  analysisClientStateId: string,
  step: GetAnalysisPipelineStep,
  inputDataReference: DataReference,
  inputDataFrameId?: string,
  outputDataReferences?: Array<DataReference>,
  rightSidebarItemState?: RightSidebarItemState
): DataFrameRetypeColumnOptions {
  const inputStep = getLegacyStepFromAnalysis(analysis, inputDataReference.stepId);
  inputDataFrameId =
    inputDataFrameId ?? findAndAssertSingleStepDataFrameId(inputStep, inputDataReference.stepDataName);

  if (step.definition.stepType === PipelineStepType.RETYPE_COLUMNS) {
    const retypeColumn = async (
      columnSystemName: string,
      newColumnType?: RetypeColumnType
    ): Promise<void> => {
      await withLoadingScreen(
        updateRetypeColumnStep(analysis, analysisClientStateId, step, columnSystemName, newColumnType),
        LOADING_MESSAGE
      );
    };
    return {retypeColumn};
  }
  // Previous step in chain is not a retype column step, so we make an new transform
  const retypeColumn = async (columnSystemName: string, newColumnType?: RetypeColumnType): Promise<void> => {
    await withLoadingScreen(
      addRetypeColumnStep(
        analysis,
        columnSystemName,
        newColumnType!,
        inputDataReference,
        inputDataFrameId,
        outputDataReferences,
        rightSidebarItemState
      ),
      LOADING_MESSAGE
    );
  };
  return {retypeColumn};
}
